import { FC, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/contexts/AuthContext';
import { inviteApi } from '@/services/api';

export const RegisterPage: FC = () => {
  const navigate = useNavigate();
  const { register, isLoading } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    inviteCode: '',
  });
  const [error, setError] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('两次输入的密码不一致');
      return;
    }
    if (formData.password.length < 6) {
      setError('密码长度不能少于 6 位');
      return;
    }

    try {
      await register(formData.email, formData.password, formData.inviteCode);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : '注册失败，请重试');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleGetInviteCode = async () => {
    setInviteLoading(true);
    setError('');
    try {
      const res = await inviteApi.generatePublic();
      setFormData(prev => ({ ...prev, inviteCode: res.code }));
    } catch (err) {
      setError(err instanceof Error ? err.message : '获取邀请码失败，请稍后重试');
    } finally {
      setInviteLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-apple-light-gray flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-[400px]">

        {/* Logo */}
        <div className="text-center mb-10">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <div className="w-10 h-10 bg-apple-dark rounded-apple flex items-center justify-center">
              <span className="text-white font-bold">白</span>
            </div>
            <span
              className="text-xl font-semibold text-apple-dark"
              style={{ letterSpacing: '-0.02em' }}
            >
              白茆AI
            </span>
          </Link>
          <h1
            className="font-bold text-apple-dark mb-1.5"
            style={{ fontSize: '1.75rem', letterSpacing: '-0.025em' }}
          >
            创建账户
          </h1>
          <p className="text-[15px] text-apple-gray">加入白茆AI，开启智能编程之旅</p>
        </div>

        {/* 表单卡片 */}
        <div className="bg-white rounded-apple-xl shadow-apple-md p-8">
          <form onSubmit={handleSubmit} className="space-y-4">

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-apple-dark mb-1.5">
                邮箱地址
              </label>
              <input
                id="email" name="email" type="email" autoComplete="email" required
                value={formData.email} onChange={handleChange}
                className="input-apple" placeholder="name@example.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-apple-dark mb-1.5">
                密码
              </label>
              <input
                id="password" name="password" type="password" autoComplete="new-password" required
                value={formData.password} onChange={handleChange}
                className="input-apple" placeholder="至少 6 位"
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-apple-dark mb-1.5">
                确认密码
              </label>
              <input
                id="confirmPassword" name="confirmPassword" type="password" autoComplete="new-password" required
                value={formData.confirmPassword} onChange={handleChange}
                className="input-apple" placeholder="再次输入密码"
              />
            </div>

            <div>
              <label htmlFor="inviteCode" className="block text-sm font-medium text-apple-dark mb-1.5">
                邀请码
                <span className="text-apple-gray font-normal ml-1 text-xs">（必填）</span>
              </label>
              <div className="flex gap-2">
                <input
                  id="inviteCode" name="inviteCode" type="text"
                  value={formData.inviteCode} onChange={handleChange}
                  className="input-apple flex-1" placeholder="输入邀请码"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleGetInviteCode}
                  disabled={inviteLoading}
                  className="whitespace-nowrap"
                >
                  {inviteLoading ? '获取中…' : '获取邀请码'}
                </Button>
              </div>
              <p className="mt-1.5 text-xs text-apple-gray">没有邀请码？点击右侧按钮即可领取</p>
            </div>

            {/* 错误提示 */}
            {error && (
              <div className="flex items-start gap-2.5 p-3.5 rounded-apple bg-[#FFF2F0] border border-[#FFCCC7]">
                <svg className="w-4 h-4 text-apple-red flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-apple-red">{error}</p>
              </div>
            )}

            <Button type="submit" className="w-full justify-center" disabled={isLoading} size="lg">
              {isLoading ? '注册中…' : '创建账户'}
            </Button>
          </form>

          {/* 注册须知 */}
          <div className="mt-5 p-4 rounded-apple bg-apple-light-gray">
            <p className="text-xs font-medium text-apple-dark mb-2">注册须知</p>
            <ul className="space-y-1">
              {[
                '注册后需要客服开通服务权限',
                '支持邀请码注册，享受额外优惠',
                '所有功能遵循订阅套餐限制',
              ].map((item) => (
                <li key={item} className="flex items-center gap-1.5 text-xs text-apple-gray">
                  <span className="w-1 h-1 rounded-full bg-apple-gray flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* 分隔线 */}
          <div className="my-5 flex items-center gap-3">
            <div className="flex-1 h-px bg-apple-separator" />
            <span className="text-sm text-apple-gray">或者</span>
            <div className="flex-1 h-px bg-apple-separator" />
          </div>

          {/* 登录入口 */}
          <p className="text-center text-[15px] text-apple-gray">
            已有账户？{' '}
            <Link to="/login" className="text-apple-blue hover:underline font-medium">
              立即登录
            </Link>
          </p>
        </div>

        {/* 底部辅助链接 */}
        <div className="mt-6 flex flex-col items-center gap-3">
          <Link to="/contact" className="text-sm text-apple-gray hover:text-apple-dark transition-colors">
            需要帮助？联系客服
          </Link>
          <p className="text-xs text-apple-gray text-center">
            注册即代表同意{' '}
            <a href="#" className="text-apple-blue hover:underline">服务条款</a>
            {' '}和{' '}
            <a href="#" className="text-apple-blue hover:underline">隐私政策</a>
          </p>
        </div>
      </div>
    </div>
  );
};
